import { useState } from 'react'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { useQuery } from '@tanstack/react-query'

import { CopyableId } from '../../components/ui/CopyableId'
import { DataState } from '../../components/DataState'
import { PageHeader } from '../../components/ui/PageHeader'
import { Pager } from '../../components/ui/Pager'
import { StatusChip, statusLabel } from '../../components/ui/StatusChip'
import { RequestDetail } from './RequestDetail'
import { requestMoney } from './money'
import { UNSET, formatDateTime, formatDuration, formatIrr } from '../../format'
import { listApplications, listRequests, type ApiRequestRow } from '../../api/portal'
import { usePortal } from '../../auth/sessionContext'
import { Ltr } from '../../components/ui/Ltr'
import Typography from '@mui/material/Typography'

/** The statuses a request can end in, as the server stores them. */
const STATUSES = ['received', 'in_progress', 'succeeded', 'failed', 'rejected'] as const

export function RequestsPage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [application, setApplication] = useState('')
  const [selected, setSelected] = useState<ApiRequestRow | null>(null)

  const filters = { ...(status ? { status } : {}), ...(application ? { application } : {}) }

  const query = useQuery({
    queryKey: ['client', 'requests', customerId, page, status, application],
    queryFn: ({ signal }) => listRequests(customerId, page, filters, signal),
  })

  // Only to name the applications in the filter. The request rows already
  // carry the name they need to display.
  const applications = useQuery({
    queryKey: ['client', 'applications', customerId],
    queryFn: ({ signal }) => listApplications(customerId, signal),
  })

  /** Any filter change invalidates the page number: page 4 of a narrower
   *  result set is usually empty, which reads as "no requests". */
  function narrow(change: () => void) {
    change()
    setPage(1)
  }

  return (
    <Box>
      <PageHeader
        title="مصرف و درخواست‌ها"
        description="هر فراخوانی که اپلیکیشن‌های شما انجام داده‌اند، نتیجهٔ آن، بهای اعلام‌شده، مبلغ کسرشده، بازگشت احتمالی و اثر نهایی روی موجودی. محتوای درخواست و پاسخ نگهداری نمی‌شود."
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="وضعیت"
          value={status}
          onChange={(event) => narrow(() => setStatus(event.target.value))}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">همهٔ وضعیت‌ها</MenuItem>
          {STATUSES.map((option) => (
            <MenuItem key={option} value={option}>
              {statusLabel(option)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="اپلیکیشن"
          value={application}
          onChange={(event) => narrow(() => setApplication(event.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">همهٔ اپلیکیشن‌ها</MenuItem>
          {applications.data?.results.map((row) => (
            <MenuItem key={row.id} value={row.id}>
              {row.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={query.data?.results.length === 0}
        emptyMessage={
          status || application
            ? 'درخواستی با این فیلترها پیدا نشد.'
            : 'هنوز درخواستی ثبت نشده است. به محض اولین فراخوانی، اینجا نمایش داده می‌شود.'
        }
        onRetry={() => void query.refetch()}
      >
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="درخواست‌ها">
            <TableHead>
              <TableRow>
                <TableCell>زمان</TableCell>
                <TableCell>شناسهٔ پیگیری</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>اپلیکیشن</TableCell>
                <TableCell>سرویس</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell align="right">بهای اعلام‌شده</TableCell>
                <TableCell align="right">کسرشده</TableCell>
                <TableCell align="right">بازگشتی</TableCell>
                <TableCell align="right">اثر بر موجودی</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  مدت
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data?.results.map((row) => {
                const money = requestMoney(row)
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelected(row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    {/* Copying the id must not also open the drawer: this is
                        the cell somebody clicks while writing a support
                        message, not while browsing. */}
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <CopyableId value={row.request_id} visible={8} label="شناسهٔ پیگیری" />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {row.application_name}
                    </TableCell>
                    {/* The name first, the code under it. A history page whose
                        only label is `banking.card_to_iban` asks the reader to
                        translate their own invoice. */}
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="body2">
                        {row.operation_display_name_fa || row.operation_code}
                      </Typography>
                      {/* Only when it adds something. A server that sends no
                          Persian name falls back to the code above, and
                          printing it twice is noise, not detail. */}
                      {row.operation_display_name_fa ? (
                        <Ltr
                          component="code"
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {row.operation_code}
                        </Ltr>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatIrr(money.quotedIrr)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatIrr(money.chargedIrr)}
                    </TableCell>
                    {/* An em dash, not «۰ ریال»: no refund happened, which is
                        a different fact from a refund of nothing. */}
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money.refundedIrr > 0 ? formatIrr(money.refundedIrr) : UNSET.amount}
                    </TableCell>
                    {/* Sign first, colour second: a column that separates
                        money out from money back by colour alone is
                        unreadable to a colour-blind customer. */}
                    <TableCell align="right">
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          color: money.netIrr > 0 ? 'success.main' : 'text.primary',
                        }}
                      >
                        {money.netIrr > 0 ? '+\u200e' : ''}
                        {formatIrr(money.netIrr)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {formatDuration(row.duration_ms)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pager page={page} count={query.data?.count ?? 0} onChange={setPage} />
      </DataState>

      <RequestDetail request={selected} onClose={() => setSelected(null)} />
    </Box>
  )
}
